import { z } from 'zod'
import { asObjectType, asEnumType } from '@gqloom/zod'
import { fileConfigFieldSchema } from '../file-upload/file-upload.schemas'
import {
  createPaginationWithCursorArgsSchema,
  createPaginationWithCursorResultSchema,
} from '../database'
import { numericSchema } from '@hapana/shared/backend/spanner'
import { SessionAggregatorConfigCreateSchema } from '../classpass/session-aggregator-config.schema'

// Simple image upload state schema (no GraphQL registration to avoid conflicts)
// Reuses existing fileConfigFieldSchema and extends it with versions
export const sessionImageVersionSchema = z.object({
  type: z.string(), // e.g., 'original', 'thumbnail', 'small', 'medium', 'large'
  url: z.string(),
  width: z.number().optional(),
  height: z.number().optional(),
  format: z.string().optional(), // e.g., 'jpeg', 'webp', 'png'
})

export const sessionImageSchema = fileConfigFieldSchema
  .extend({
    versions: z.array(sessionImageVersionSchema).optional(),
  })
  .partial()

export const SessionCategoryEnum = z.enum(['CLASS', 'PRIVATE', 'WORKSHOP']).register(asEnumType, {
  name: 'SessionCategory',
  description: 'Session category types',
})

export const SiteWorkshopTypeEnum = z
  .enum(['SINGLE', 'RECURRING', 'CONTINUOUS'])
  .register(asEnumType, {
    name: 'SiteWorkshopTypes',
    description: 'Workshop category filter for the Website Widgets configurator',
  })

export const SiteWorkshopSchema = z
  .object({
    sessionId: z.string(),
    name: z.string(),
    date: z.iso.date(),
    startTime: z.iso.datetime(),
  })
  .register(asObjectType, {
    name: 'SiteWorkshop',
    description: 'Upcoming workshop occurrence used to populate the Website Widgets dropdown',
  })

export type SiteWorkshop = z.infer<typeof SiteWorkshopSchema>

// Site Session Types
export const CustomColorSchema = z.object({
  colorName: z.string(),
  hexCode: z.string().regex(/^#[0-9A-F]{6}$/i),
})

export const SiteSessionTypeSchema = z
  .object({
    siteSessionTypeId: z.string(),
    siteId: z.string(),
    name: z.string().min(1).max(255),
    color: z
      .string()
      .regex(/^#[0-9A-F]{6}$/i)
      .nullable(),
    allowMultiLocationAccess: z.boolean().default(false),
    allowEditingSessionType: z.boolean().default(false),
    customColors: z.array(CustomColorSchema).nullable().optional(),
    isSystemManaged: z.boolean().default(false),
    displayOrder: z.number().int().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    deletedAt: z.string().nullable().optional(),
  })
  .register(asObjectType, {
    name: 'SiteSessionType',
    description: 'A session type for a site',
  })

export const SiteSessionTypeCreateSchema = z
  .object({
    name: z.string().min(1, 'Session type name is required'),
    color: z.string().regex(/^#[0-9A-F]{6}$/i, 'Color must be a valid hex color'),
    allowMultiLocationAccess: z.boolean().default(false),
    allowEditingSessionType: z.boolean().default(false),
    customColors: z.array(CustomColorSchema).nullable().optional(),
    displayOrder: z.number().int().optional(),
  })
  .register(asObjectType, {
    name: 'SiteSessionTypeInput',
    description: 'Input for creating a site session type',
  })

export const SiteSessionTypeUpdateSchema = SiteSessionTypeCreateSchema.partial().register(
  asObjectType,
  {
    name: 'SiteSessionTypeUpdateInput',
    description: 'Input for updating a site session type',
  }
)

export const reorderSiteSessionTypesInputSchema = z
  .object({
    siteSessionTypeId: z.string(),
    displayOrder: z.number().int().nonnegative(),
  })
  .register(asObjectType, {
    name: 'ReorderSiteSessionTypeInput',
    description: 'Input for reordering a single site session type',
  })

export const reorderSiteSessionTypesArgsSchema = z
  .object({
    sessionTypes: z.array(reorderSiteSessionTypesInputSchema).min(1),
  })
  .register(asObjectType, {
    name: 'ReorderSiteSessionTypesInput',
    description: 'Input containing array of session types with new display orders',
  })

export const siteSessionTypeCreateSchema = SiteSessionTypeCreateSchema
export const siteSessionTypeUpdateSchema = SiteSessionTypeUpdateSchema

export type SiteSessionType = z.infer<typeof SiteSessionTypeSchema>
export type SiteSessionTypeCreateInput = z.infer<typeof SiteSessionTypeCreateSchema>
export type SiteSessionTypeUpdateInput = z.infer<typeof SiteSessionTypeUpdateSchema>
export type CustomColor = z.infer<typeof CustomColorSchema>
export type ReorderSiteSessionTypesInput = z.infer<typeof reorderSiteSessionTypesInputSchema>
export type ReorderSiteSessionTypesArgs = z.infer<typeof reorderSiteSessionTypesArgsSchema>

// Simplified schemas for Session field resolvers (avoids N+1 queries)
export const SessionSiteSessionTypeSchema = z
  .object({
    name: z.string(),
    color: z.string().nullable(),
  })
  .register(asObjectType, {
    name: 'SessionSiteSessionType',
    description: 'Simplified session type for session field resolver',
  })

export const SessionLocationSchema = z
  .object({
    name: z.string(),
  })
  .register(asObjectType, {
    name: 'SessionLocation',
    description: 'Simplified location for session field resolver',
  })

export const SessionRoomSchema = z
  .object({
    name: z.string(),
    hasResourceBooking: z.boolean().optional(),
    roomResourceTemplateId: z.string().nullable().optional(),
  })
  .register(asObjectType, {
    name: 'SessionRoom',
    description: 'Simplified room for session field resolver',
  })

// Session Template Custom Field Schema
// id is optional to tolerate V1-migrated data that uses {name, value} instead of {id, title, information, enabled}
export const SessionTemplateCustomFieldSchema = z.object({
  id: z.string().optional(),
  title: z.string().max(100).optional(),
  information: z.string().max(500).optional(),
  enabled: z.boolean().default(true),
  // V1 legacy fields — some migrated templates still use this shape
  name: z.string().optional(),
  value: z.string().optional(),
})

// Custom Cancel Policy Schema
export const CustomCancelPolicySchema = z.object({
  days: z.number().int().min(0).default(0),
  hours: z.number().int().min(0).default(0),
  minutes: z.number().int().min(0).default(0),
})

// Custom Release Schedule Schema
export const CustomReleaseScheduleSchema = z.object({
  releaseType: z.string(), // e.g., 'ROLLING_RELEASE', 'TIMED_RELEASE'
  bookingAdvanceDays: z.number().int().min(0).default(0),
  bookingAdvanceHours: z.number().int().min(0).default(0),
  dayOfWeek: z.string().nullable().optional(),
  time: z.string().nullable().optional(),
  intervalValue: z.number().int().min(0).optional(),
  intervalType: z.string().optional(),
  hoursBeforeStart: z.number().int().min(0).optional(),
})

// Session Template Schema
export const SessionTemplateSchema = z
  .object({
    sessionTemplateId: z.string(),
    siteId: z.string(),
    name: z.string().min(1).max(255),
    description: z.string().optional(),
    category: SessionCategoryEnum,
    durationHours: z.number().int().min(0).default(0),
    durationMinutes: z.number().int().min(0).max(59).default(0),
    siteSessionTypeId: z.string(),
    size: z.number().int().min(1).default(1),
    creditsRequired: z.number().int().min(0).default(0),
    dropInPrice: numericSchema,
    taxEnabled: z.boolean().default(false),
    taxSettingIds: z.array(z.string()).optional(),
    siteLocationId: z.string(),
    siteLocationRoomId: z.string().nullable().optional(),
    sessionImage: sessionImageSchema.nullable().optional(), // Image upload state
    itemsSupplied: z.string().max(1000).nullable().optional(),
    itemsToBring: z.string().max(1000).nullable().optional(),
    customFields: z.array(SessionTemplateCustomFieldSchema).default([]),
    isRequestBookingEnabled: z.boolean().default(false),
    isPaymentRequired: z.boolean().default(false),
    isRequiredSessionFeedback: z.boolean().default(false),
    isRequiredTwoPartAuthentication: z.boolean().default(false),
    allowOverlappingSessions: z.boolean().default(true),
    waitlistEnabled: z.boolean().default(false),
    waitlistSize: z.number().int().min(1).nullable().optional(),
    customCancelPolicy: CustomCancelPolicySchema.nullable().optional(),
    customReleaseSchedule: CustomReleaseScheduleSchema.nullable().optional(),
    availableForStaffUserIds: z.array(z.string()).optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    deletedAt: z.string().nullable().optional(),
  })
  .register(asObjectType, {
    name: 'SessionTemplate',
    description: 'A session template for creating sessions',
  })

export const SessionTemplateCreateSchema = z
  .object({
    name: z.string().min(1, 'Session template name is required').max(255),
    description: z.string().optional(),
    category: SessionCategoryEnum,
    durationHours: z.number().int().min(0).default(0),
    durationMinutes: z.number().int().min(0).max(59).default(0),
    siteSessionTypeId: z.string().min(1, 'Session type is required'),
    size: z.number().int().min(1).default(1),
    creditsRequired: z.number().int().min(0).default(0),
    dropInPrice: numericSchema,
    taxEnabled: z.boolean().default(false),
    taxSettingIds: z.array(z.string()).optional(),
    siteLocationId: z.string().min(1, 'Location is required'),
    siteLocationRoomId: z.string().nullable().optional(),
    sessionImage: sessionImageSchema.nullable().optional(),
    itemsSupplied: z.string().max(1000).nullable().optional(),
    itemsToBring: z.string().max(1000).nullable().optional(),
    customFields: z.array(SessionTemplateCustomFieldSchema).default([]),
    isRequestBookingEnabled: z.boolean().default(false),
    isPaymentRequired: z.boolean().default(false),
    isRequiredSessionFeedback: z.boolean().default(false),
    isRequiredTwoPartAuthentication: z.boolean().default(false),
    allowOverlappingSessions: z.boolean().optional(),
    waitlistEnabled: z.boolean().default(false),
    waitlistSize: z.number().int().min(1).optional(),
    customCancelPolicy: CustomCancelPolicySchema.nullable().optional(),
    customReleaseSchedule: CustomReleaseScheduleSchema.nullable().optional(),
    availableForStaffUserIds: z.array(z.string()).optional(),
  })
  .register(asObjectType, {
    name: 'SessionTemplateInput',
    description: 'Input for creating a session template',
  })

export const SessionTemplateUpdateSchema = SessionTemplateCreateSchema.partial()
  .omit({ siteLocationId: true, availableForStaffUserIds: true }) // Don't allow changing location or staff users in general updates
  .register(asObjectType, {
    name: 'SessionTemplateUpdateInput',
    description: 'Input for updating a session template',
  })

// Schema for managing staff user access (used for both add and remove operations)
export const SessionTemplateStaffUserSchema = z
  .object({
    staffUserId: z.string().min(1, 'Staff user ID is required'),
  })
  .register(asObjectType, {
    name: 'SessionTemplateStaffUserInput',
    description: 'Input for managing staff user access to a session template',
  })

export const sessionTemplateCreateSchema = SessionTemplateCreateSchema
export const sessionTemplateUpdateSchema = SessionTemplateUpdateSchema
export const sessionTemplateStaffUserSchema = SessionTemplateStaffUserSchema

export const SessionTemplatePaginationArgsSchema = createPaginationWithCursorArgsSchema()
  .withSearch()
  .build()
  .extend({
    siteId: z.string(),
    category: SessionCategoryEnum.optional(),
    siteSessionTypeId: z.string().optional(),
  })
  .register(asObjectType, {
    name: 'SessionTemplatePaginationArgs',
    description: 'Arguments for paginated session template queries',
  })

export const SessionTemplatePaginationResultSchema = createPaginationWithCursorResultSchema(
  SessionTemplateSchema
).register(asObjectType, {
  name: 'SessionTemplatePaginationResult',
  description: 'Paginated result for session templates',
})

export const ApplySessionTemplateResultSchema = z
  .object({
    sessionTemplate: SessionTemplateSchema,
    updatedSessionCount: z.number().int(),
    skippedSessionCount: z.number().int(),
    updatedRecurringPatternCount: z.number().int(),
  })
  .register(asObjectType, {
    name: 'ApplySessionTemplateResult',
    description: 'Result of applying template changes to future sessions',
  })

export type SessionTemplate = z.infer<typeof SessionTemplateSchema>
export type SessionTemplateCreateInput = z.infer<typeof SessionTemplateCreateSchema>
export type SessionTemplateUpdateInput = z.infer<typeof SessionTemplateUpdateSchema>
export type SessionTemplateStaffUserInput = z.infer<typeof SessionTemplateStaffUserSchema>
export type SessionTemplateCustomField = z.infer<typeof SessionTemplateCustomFieldSchema>
export type CustomCancelPolicy = z.infer<typeof CustomCancelPolicySchema>
export type CustomReleaseSchedule = z.infer<typeof CustomReleaseScheduleSchema>
export type SessionCategory = z.infer<typeof SessionCategoryEnum>
export type SessionTemplatePaginationArgs = z.infer<typeof SessionTemplatePaginationArgsSchema>
export type SessionTemplatePaginationResult = z.infer<typeof SessionTemplatePaginationResultSchema>
export type ApplySessionTemplateResult = z.infer<typeof ApplySessionTemplateResultSchema>
export type SessionImageVersion = z.infer<typeof sessionImageVersionSchema>
export type SessionImage = z.infer<typeof sessionImageSchema>

export const SessionStatus = {
  Scheduled: 'SCHEDULED',
  InProgress: 'IN_PROGRESS',
  Completed: 'COMPLETED',
  Cancelled: 'CANCELLED',
} as const

export const SessionStatusEnum = z
  .enum([
    SessionStatus.Scheduled,
    SessionStatus.InProgress,
    SessionStatus.Completed,
    SessionStatus.Cancelled,
  ])
  .register(asEnumType, {
    name: 'SessionStatus',
    description: 'Session status types',
  })

export const BookingAvailability = {
  Open: 'OPEN',
  Waitlist: 'WAITLIST',
  Full: 'FULL',
  Closed: 'CLOSED',
  Cancelled: 'CANCELLED',
  Completed: 'COMPLETED',
} as const

export const BookingAvailabilityEnum = z.enum(BookingAvailability).register(asEnumType, {
  name: 'BookingAvailability',
  description: 'Computed booking availability for a session',
})

export const WaitlistInvitationStrategyEnum = z
  .enum(['ALL', 'TOP_N', 'DYNAMIC'])
  .register(asEnumType, {
    name: 'WaitlistInvitationStrategy',
    description: 'Waitlist invitation strategy types',
  })

export const SessionSchema = z
  .object({
    sessionId: z.string(),
    siteId: z.string(),
    sessionTemplateId: z.string().nullable(),
    recurringSessionsId: z.string().nullable(),
    recurrenceDate: z.coerce.date().nullable(),
    isModified: z.boolean(),
    name: z.string().min(1).max(255),
    description: z.string().nullable(),
    category: SessionCategoryEnum,
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format'),
    startTime: z.coerce.date(),
    endTime: z.coerce.date(),
    siteSessionTypeId: z.string(),
    siteLocationId: z.string(),
    siteLocationRoomId: z.string().nullable(),
    size: z.number().int().min(0),
    creditsRequired: z.number().int().min(0),
    dropinPrice: numericSchema,
    taxEnabled: z.boolean(),
    taxSettingIds: z.array(z.string()).nullable(),
    sessionImage: sessionImageSchema.nullable(),
    itemsSupplied: z.string().nullable(),
    itemsToBring: z.string().nullable(),
    customFields: z.array(SessionTemplateCustomFieldSchema).nullable(),
    isRequestBookingEnabled: z.boolean(),
    isPaymentRequired: z.boolean(),
    isRequiredSessionFeedback: z.boolean(),
    isRequiredTwoPartAuthentication: z.boolean(),
    allowOverlappingSessions: z.boolean().default(true),
    waitlistEnabled: z.boolean(),
    waitlistSize: z.number().int().min(0).nullable(),
    waitlistInvitationStrategy: WaitlistInvitationStrategyEnum.nullable(),
    waitlistBatchSize: z.number().int().min(0).nullable(),
    waitlistInvitationTimeout: z.number().int().min(0).nullable(),
    customCancelPolicy: CustomCancelPolicySchema.nullable().optional(),
    customReleaseSchedule: CustomReleaseScheduleSchema.nullable().optional(),
    status: SessionStatusEnum,
    isBlockSlot: z.boolean().default(false),
    bookedCount: z.number().int().min(0).nullable(),
    disabledSpotIds: z.array(z.number().int()).nullable(),
    isImported: z.boolean(),
    createdAt: z.coerce.date().nullable(),
    updatedAt: z.coerce.date().nullable(),
    deletedAt: z.coerce.date().nullable(),
    deletedBy: z.string().nullable().optional(),
    // Computed fields from JOINs to avoid N+1 queries
    locationName: z.string().nullable().optional(),
    roomName: z.string().nullable().optional(),
    roomHasResourceBooking: z.boolean().nullable().optional(),
    roomResourceTemplateId: z.string().nullable().optional(),
    sessionTypeName: z.string().nullable().optional(),
    sessionTypeColor: z.string().nullable().optional(),
  })
  .register(asObjectType, {
    name: 'Session',
    description: 'A scheduled session',
  })

export const SessionCreateFromTemplateSchema = z
  .object({
    sessionTemplateId: z.string().min(1, 'Session template ID is required'),
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format'),
    startTime: z.coerce.date(),
    endTime: z.coerce.date().optional(),
    siteLocationId: z.string().nullable().optional(),
    siteLocationRoomId: z.string().nullable().optional(),
    name: z.string().max(255).nullable().optional(),
    description: z.string().nullable().optional(),
    size: z.number().int().min(1).nullable().optional(),
    creditsRequired: z.number().int().min(0).nullable().optional(),
    dropinPrice: numericSchema.nullable().optional(),
    staffUserIds: z.array(z.string()).nullable().optional(),
    recurringSessionsId: z.string().nullable().optional(),
    recurrenceDate: z.coerce.date().nullable().optional(),
    isModified: z.boolean().default(false),
    aggregatorConfigs: z.array(SessionAggregatorConfigCreateSchema).nullable().optional(),
  })
  .register(asObjectType, {
    name: 'SessionCreateFromTemplateInput',
    description: 'Input for creating a session from a template',
  })

export const SessionCreateCustomSchema = z
  .object({
    sessionTemplateId: z.string().nullable().optional(),
    recurringSessionsId: z.string().nullable().optional(),
    recurrenceDate: z.coerce.date().nullable().optional(),
    isModified: z.boolean().default(false),
    name: z.string().min(1, 'Session name is required').max(255),
    description: z.string().nullable().optional(),
    category: SessionCategoryEnum,
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format'),
    startTime: z.coerce.date(),
    endTime: z.coerce.date(),
    siteSessionTypeId: z.string().min(1, 'Session type is required'),
    siteLocationId: z.string().min(1, 'sessions.addSessionDialog.validation.locationRequired'),
    siteLocationRoomId: z.string().nullable().optional(),
    size: z.number().int().min(1).default(1),
    creditsRequired: z.number().int().min(0).default(0),
    dropinPrice: numericSchema,
    taxEnabled: z.boolean().default(false),
    taxSettingIds: z.array(z.string()).nullable().optional(),
    sessionImage: sessionImageSchema.nullable().optional(),
    itemsSupplied: z.string().nullable().optional(),
    itemsToBring: z.string().nullable().optional(),
    customFields: z.array(SessionTemplateCustomFieldSchema).default([]),
    isRequestBookingEnabled: z.boolean().default(false),
    isPaymentRequired: z.boolean().default(false),
    isRequiredSessionFeedback: z.boolean().default(false),
    isRequiredTwoPartAuthentication: z.boolean().default(false),
    allowOverlappingSessions: z.boolean().optional(),
    waitlistEnabled: z.boolean().default(false),
    waitlistSize: z.number().int().min(1).nullable().optional(),
    waitlistInvitationStrategy: WaitlistInvitationStrategyEnum.nullable().optional(),
    waitlistBatchSize: z.number().int().min(1).nullable().optional(),
    waitlistInvitationTimeout: z.number().int().min(0).nullable().optional(),
    customCancelPolicy: CustomCancelPolicySchema.nullable().optional(),
    customReleaseSchedule: CustomReleaseScheduleSchema.nullable().optional(),
    status: SessionStatusEnum.default('SCHEDULED'),
    isBlockSlot: z.boolean().optional().default(false),
    staffUserIds: z.array(z.string()).nullable().optional(),
    disabledSpotIds: z.array(z.number().int()).nullable().optional(),
    aggregatorConfigs: z.array(SessionAggregatorConfigCreateSchema).nullable().optional(),
  })
  .register(asObjectType, {
    name: 'SessionCreateCustomInput',
    description: 'Input for creating a custom session',
  })

export const SessionCreateSchema = SessionCreateCustomSchema

export const SessionUpdateSchema = SessionCreateSchema.omit({
  isModified: true,
  size: true,
  creditsRequired: true,
  dropinPrice: true,
  taxEnabled: true,
  customFields: true,
  isRequestBookingEnabled: true,
  isPaymentRequired: true,
  isRequiredSessionFeedback: true,
  isRequiredTwoPartAuthentication: true,
  waitlistEnabled: true,
  status: true,
})
  .extend({
    isModified: z.boolean().optional(),
    size: z.number().int().min(1).optional(),
    creditsRequired: z.number().int().min(0).optional(),
    dropinPrice: numericSchema.optional(),
    taxEnabled: z.boolean().optional(),
    customFields: z.array(SessionTemplateCustomFieldSchema).optional(),
    isRequestBookingEnabled: z.boolean().optional(),
    isPaymentRequired: z.boolean().optional(),
    isRequiredSessionFeedback: z.boolean().optional(),
    isRequiredTwoPartAuthentication: z.boolean().optional(),
    waitlistEnabled: z.boolean().optional(),
    status: SessionStatusEnum.optional(),
    isBlockSlot: z.boolean().optional(),
    aggregatorConfigs: z.array(SessionAggregatorConfigCreateSchema).nullable().optional(),
  })
  .partial()
  .register(asObjectType, {
    name: 'SessionUpdateInput',
    description: 'Input for updating a session',
  })

export const BlockSlotCreateSchema = z
  .object({
    name: z.string().min(1, 'Block slot name is required').max(255),
    description: z.string().nullable().optional(),
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format'),
    startTime: z.coerce.date(),
    endTime: z.coerce.date(),
    siteLocationId: z.string().min(1, 'Location is required'),
    siteLocationRoomId: z.string().nullable().optional(),
    // Optional staff user IDs for instructor-specific blocks (Team View)
    // If provided, block only applies to these instructors
    // If empty/null, block is site-wide (Calendar View) or room-specific (Room View)
    staffUserIds: z.array(z.string()).nullable().optional(),
  })
  .register(asObjectType, {
    name: 'BlockSlotCreateInput',
    description: 'Input for creating a block slot',
  })

export type BlockSlotCreateInput = z.infer<typeof BlockSlotCreateSchema>

export const SubstituteInstructorInputSchema = z
  .object({
    sessionId: z.string(),
    instructorToRemoveId: z.string(),
    substituteInstructorId: z.string(),
  })
  .register(asObjectType, {
    name: 'SubstituteInstructorInput',
    description: 'Input for substituting an instructor in a session',
  })

export const NotificationMethodEnum = z.enum(['email', 'sms', 'push']).register(asEnumType, {
  name: 'NotificationMethod',
  description: 'Notification method types',
})

export const CancelSessionScopeEnum = z.enum(['single', 'future']).register(asEnumType, {
  name: 'CancelSessionScope',
  description: 'Scope of session cancellation',
})

export const CancelSessionInputSchema = z
  .object({
    sessionId: z.string(),
    siteId: z.string(),
    scope: CancelSessionScopeEnum,
    notificationMethods: z.array(NotificationMethodEnum),
    subject: z.string().max(200).optional(),
    message: z.string().max(1000).optional(),
    // When true with scope=future, also cancels and soft-deletes downstream
    // split siblings. Defaults to bounded (current segment only).
    overrideDownstreamSplits: z.boolean().optional(),
  })
  .refine(
    (data) => {
      if (data.notificationMethods.length > 0) {
        return data.subject && data.subject.trim().length > 0
      }
      return true
    },
    { message: 'Subject is required when sending notifications', path: ['subject'] }
  )
  .refine(
    (data) => {
      if (data.notificationMethods.length > 0) {
        return data.message && data.message.trim().length > 0
      }
      return true
    },
    { message: 'Message is required when sending notifications', path: ['message'] }
  )
  .register(asObjectType, {
    name: 'CancelSessionInput',
    description: 'Input for cancelling a session',
  })

export type CancelSessionInput = z.infer<typeof CancelSessionInputSchema>
export type NotificationMethod = z.infer<typeof NotificationMethodEnum>
export type CancelSessionScope = z.infer<typeof CancelSessionScopeEnum>

export const CancelSessionsBatchInputSchema = z
  .object({
    sessionIds: z.array(z.string()).min(1, 'At least one session ID required'),
    siteId: z.string(),
    notificationMethods: z.array(NotificationMethodEnum),
    subject: z.string().max(200).optional(),
    message: z.string().max(1000).optional(),
  })
  .refine(
    (data) => {
      if (data.notificationMethods.length > 0) {
        return data.subject && data.subject.trim().length > 0
      }
      return true
    },
    { message: 'Subject is required when sending notifications', path: ['subject'] }
  )
  .refine(
    (data) => {
      if (data.notificationMethods.length > 0) {
        return data.message && data.message.trim().length > 0
      }
      return true
    },
    { message: 'Message is required when sending notifications', path: ['message'] }
  )
  .register(asObjectType, {
    name: 'CancelSessionsBatchInput',
    description: 'Input for batch cancelling multiple sessions',
  })

export type CancelSessionsBatchInput = z.infer<typeof CancelSessionsBatchInputSchema>

export const InstructorForSessionSchema = z
  .object({
    staffUserId: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
    status: z.string(),
  })
  .register(asObjectType, {
    name: 'InstructorForSession',
    description: 'Staff user who can be assigned as an instructor',
  })

export type InstructorForSession = z.infer<typeof InstructorForSessionSchema>

export const sessionCreateFromTemplateSchema = SessionCreateFromTemplateSchema
export const sessionCreateCustomSchema = SessionCreateCustomSchema
export const sessionCreateSchema = SessionCreateSchema
export const sessionUpdateSchema = SessionUpdateSchema

export const SessionPaginationArgsSchema = createPaginationWithCursorArgsSchema()
  .withSearch()
  .build()
  .extend({
    siteId: z.string(),
    category: SessionCategoryEnum.optional(),
    siteSessionTypeId: z.string().optional(),
    siteLocationId: z.string().optional(),
    status: SessionStatusEnum.optional(),
    startDate: z.coerce.date().optional(),
    endDate: z.coerce.date().optional(),
  })
  .register(asObjectType, {
    name: 'SessionPaginationArgs',
    description: 'Arguments for paginated session queries',
  })

export const SessionPaginationResultSchema = createPaginationWithCursorResultSchema(
  SessionSchema
).register(asObjectType, {
  name: 'SessionPaginationResult',
  description: 'Paginated result for sessions',
})

export const CalendarSessionInstructorSchema = z
  .object({
    staffUserId: z.string(),
    name: z.string().nullable(),
  })
  .register(asObjectType, {
    name: 'CalendarSessionInstructor',
    description: 'Lightweight instructor data for calendar display',
  })

export const CalendarSessionBookingSchema = z
  .object({
    clientName: z.string().nullable(),
  })
  .register(asObjectType, {
    name: 'CalendarSessionBooking',
    description: 'Minimal booking data for private session display',
  })

export const CalendarSessionSchema = z
  .object({
    sessionId: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    startTime: z.coerce.date(),
    endTime: z.coerce.date(),
    siteLocationRoomId: z.string().nullable(),
    size: z.number().int(),
    waitlistEnabled: z.boolean(),
    status: SessionStatusEnum,
    staffUserIds: z.array(z.string()).nullable(),
    bookedCount: z.number().int().nullable(),
    waitlistCount: z.number().int(),
    isBlockSlot: z.boolean(),
    roomName: z.string().nullable(),
    sessionTypeColor: z.string().nullable(),
    instructor: z.array(CalendarSessionInstructorSchema).nullable(),
    bookings: z.array(CalendarSessionBookingSchema).nullable(),
  })
  .register(asObjectType, {
    name: 'CalendarSession',
    description: 'Lightweight session data optimized for calendar/scheduler display',
  })

export type CalendarSession = z.infer<typeof CalendarSessionSchema>
export type CalendarSessionInstructor = z.infer<typeof CalendarSessionInstructorSchema>
export type CalendarSessionBooking = z.infer<typeof CalendarSessionBookingSchema>

export type Session = z.infer<typeof SessionSchema>

export const SessionsPageSchema = z
  .object({
    sessions: z.array(SessionSchema),
    totalCount: z.number().int(),
  })
  .register(asObjectType, {
    name: 'SessionsPage',
    description: 'Paginated sessions with total count for offset-based pagination',
  })

export type SessionsPage = z.infer<typeof SessionsPageSchema>

// Spanner row type - used for typing raw database records before parsing
export type SessionRow = {
  sessionId: string
  siteId: string
  sessionTemplateId: string | null
  recurringSessionsId: string | null
  recurrenceDate: Date | null
  isModified: boolean
  name: string
  description: string | null
  category: string
  date: string
  startTime: Date
  endTime: Date
  siteSessionTypeId: string
  siteLocationId: string
  siteLocationRoomId: string | null
  size: number
  creditsRequired: number
  dropinPrice: Big
  taxEnabled: boolean
  taxSettingIds: string[] | null
  sessionImage: unknown
  itemsSupplied: string | null
  itemsToBring: string | null
  customFields: unknown
  isRequestBookingEnabled: boolean
  isPaymentRequired: boolean
  isRequiredSessionFeedback: boolean
  isRequiredTwoPartAuthentication: boolean
  waitlistEnabled: boolean
  waitlistSize: number | null
  waitlistInvitationStrategy: string | null
  waitlistBatchSize: number | null
  waitlistInvitationTimeout: number | null
  customCancelPolicy: unknown
  status: string
  isBlockSlot: boolean
  bookedCount: number | null
  disabledSpotIds: number[] | null
  createdAt: Date
  updatedAt: Date
  deletedAt: Date | null
  deletedBy?: string | null
  // Computed fields from JOINs
  locationName?: string | null
  roomName?: string | null
  roomHasResourceBooking?: boolean | null
  roomResourceTemplateId?: string | null
  sessionTypeName?: string | null
  sessionTypeColor?: string | null
}

export type SessionCreateFromTemplateInput = z.infer<typeof SessionCreateFromTemplateSchema>
export type SessionCreateCustomInput = z.infer<typeof SessionCreateCustomSchema>
export type SessionCreateInput = z.infer<typeof SessionCreateSchema>

/**
 * Input shape for the per-row write inside `SessionsService.bulkCreateForImport`.
 *
 * Mirrors `SessionCreateCustomSchema` (the schema enforced by the per-row
 * `createCustom`) with caller-attached `sessionId` and `siteId` for retry
 * idempotency, plus optional `sessionTemplateId` so transformers that
 * resolve templates can still set the FK without rerouting through
 * `createFromTemplate`. The `endTime`, `siteLocationRoomId` resolution, and
 * other template-derived fields the per-row `createFromTemplate` computes
 * are owned by the transformer in the import path.
 *
 * Deviation from pure-mirror: the wrapper does NOT call `validateSession`
 * (overlap conflict + room/business-hours checks). Transformers skip it
 * intentionally — historical schedule imports must accept overlaps that
 * would be rejected at the UI level.
 */
export const SessionBulkCreateForImportSchema = SessionCreateCustomSchema.safeExtend({
  sessionId: z.uuid(),
  siteId: z.uuid(),
  sessionTemplateId: z.string().nullish(),
  isImported: z.boolean().optional(),
})

export type SessionBulkCreateForImportSchema = z.infer<typeof SessionBulkCreateForImportSchema>
export type SessionUpdateInput = z.infer<typeof SessionUpdateSchema>
export type SubstituteInstructorInput = z.infer<typeof SubstituteInstructorInputSchema>
export type SessionStatus = z.infer<typeof SessionStatusEnum>
export type BookingAvailability = z.infer<typeof BookingAvailabilityEnum>
export type WaitlistInvitationStrategy = z.infer<typeof WaitlistInvitationStrategyEnum>
export type SessionPaginationArgs = z.infer<typeof SessionPaginationArgsSchema>
export type SessionPaginationResult = z.infer<typeof SessionPaginationResultSchema>

// Cancelled Sessions Dialog Schemas
export const CancelledSessionsDateRangeOptionSchema = z.enum([
  'today',
  'yesterday',
  'next7days',
  'last7days',
  'custom',
])

export type CancelledSessionsDateRangeOption = z.infer<
  typeof CancelledSessionsDateRangeOptionSchema
> | null

export const CancelledSessionsDateRangeSchema = z.object({
  start: z.date(),
  end: z.date(),
})

export type CancelledSessionsDateRange = z.infer<typeof CancelledSessionsDateRangeSchema>

export const CancelledSessionInstructorSchema = z.object({
  staffUserId: z.string(),
  name: z.string().nullable(),
})

export type CancelledSessionInstructor = z.infer<typeof CancelledSessionInstructorSchema>

export const CancelledGroupedSessionSchema = z.object({
  sessionId: z.string(),
  name: z.string().nullable(),
  startTime: z.number(),
  endTime: z.number(),
  timeRange: z.string(),
  instructor: z.array(CancelledSessionInstructorSchema).nullable(),
  location: z.string().nullable().optional(),
})

export type CancelledGroupedSession = z.infer<typeof CancelledGroupedSessionSchema>

export const CancelledGroupedDaySchema = z.object({
  key: z.string(),
  label: z.string(),
  sessions: z.array(CancelledGroupedSessionSchema),
})

export type CancelledGroupedDay = z.infer<typeof CancelledGroupedDaySchema>

// Schema for cancelled session from GQty query (used in hooks)
export const CancelledSessionSchema = z.object({
  sessionId: z.string(),
  name: z.string().nullable(),
  startTime: z.union([z.string(), z.number()]).nullable(),
  endTime: z.union([z.string(), z.number()]).nullable(),
  size: z.number().nullable(),
  bookedCount: z.number().nullable(),
  instructor: z.array(CancelledSessionInstructorSchema).nullable(),
  roomName: z.string().nullable(),
})

export type CancelledSession = z.infer<typeof CancelledSessionSchema>

// ============================================================================
// Session Filter Schemas
// ============================================================================

// Filter type constants for session filters
export const SessionFilterType = {
  SessionType: 'sessionType',
  Instructor: 'instructor',
  Location: 'location',
  Room: 'room',
  Category: 'category',
  TimeRange: 'timeRange',
  Capacity: 'capacity',
  BookingStatus: 'bookingStatus',
} as const

// Logical operator constants
export const SessionFilterLogicalOperator = {
  And: 'and',
  Or: 'or',
} as const

// Visibility constants for filter views
export const SessionFilterVisibility = {
  Everyone: 'EVERYONE',
  Creator: 'CREATOR',
} as const

// Named enum schema for GraphQL
export const sessionFilterVisibilitySchema = z
  .enum(['EVERYONE', 'CREATOR'])
  .register(asEnumType, { name: 'SessionFilterVisibility' })

// Filter condition schema
export const sessionFilterConditionSchema = z.object({
  operator: z.string(),
  value: z.union([z.string(), z.array(z.string()), z.number(), z.boolean()]).optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  min: z.number().optional(),
  max: z.number().optional(),
})

export type SessionFilterCondition = z.infer<typeof sessionFilterConditionSchema>

// Filter group schema (for each filter type)
export const sessionFilterGroupSchema = z.object({
  logicalOperator: z.enum(['and', 'or']).default('and'),
  orderIndex: z.number().int().min(0).default(0),
  using: z.array(sessionFilterConditionSchema),
})

export type SessionFilterGroup = z.infer<typeof sessionFilterGroupSchema>

// Complete filters schema (record of filter type to filter group)
export const sessionFiltersSchema = z.record(z.string(), sessionFilterGroupSchema).optional()

export type SessionFilters = z.infer<typeof sessionFiltersSchema>

// Calendar mode enum for session filter views
export const sessionFilterCalendarModeSchema = z
  .enum(['calendar', 'list', 'team', 'room'])
  .register(asEnumType, { name: 'SessionFilterCalendarMode' })

// Session filter schema (matches the database table structure)
export const sessionFilterSchema = z.object({
  sessionFilterId: z.string().uuid(),
  siteId: z.string(),
  name: z.string().min(1, 'Name is required').max(255, 'Name must be 255 characters or less'),
  isVisible: z.boolean().default(true),
  isDefault: z.boolean().default(false),
  visibility: sessionFilterVisibilitySchema.default('EVERYONE'),
  calendarMode: sessionFilterCalendarModeSchema.nullable().optional(),
  sortOrder: z.number().int(),
  filters: sessionFiltersSchema,
  createdBy: z.string().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().optional(),
})

export type SessionFilterSchema = z.infer<typeof sessionFilterSchema>

// Public schema for GraphQL (excludes internal fields)
export const sessionFilterPublicSchema = sessionFilterSchema
  .omit({
    sessionFilterId: true,
    createdAt: true,
    updatedAt: true,
    deletedAt: true,
  })
  .extend({
    id: z.string(), // Maps to sessionFilterId for frontend compatibility
  })
  .register(asObjectType, {
    name: 'SessionFilter',
    description: 'Session filter with filter criteria',
  })

export type SessionFilterPublic = z.infer<typeof sessionFilterPublicSchema>

// Create schema for new filters
export const sessionFilterCreateSchema = z
  .object({
    name: z.string().min(1, 'Name is required').max(255, 'Name must be 255 characters or less'),
    isVisible: z.boolean().default(true),
    isDefault: z.boolean().default(false),
    visibility: sessionFilterVisibilitySchema.default('EVERYONE'),
    calendarMode: sessionFilterCalendarModeSchema.nullable().optional(),
    filters: sessionFiltersSchema,
  })
  .register(asObjectType, {
    name: 'SessionFilterCreateInput',
    description: 'Input for creating a session filter',
  })

export type SessionFilterCreate = z.infer<typeof sessionFilterCreateSchema>

// Update schema for existing filters
export const sessionFilterUpdateSchema = z
  .object({
    name: z
      .string()
      .min(1, 'Name is required')
      .max(255, 'Name must be 255 characters or less')
      .optional(),
    isVisible: z.boolean().optional(),
    isDefault: z.boolean().optional(),
    visibility: sessionFilterVisibilitySchema.optional(),
    calendarMode: sessionFilterCalendarModeSchema.nullable().optional(),
    filters: sessionFiltersSchema,
  })
  .register(asObjectType, {
    name: 'SessionFilterUpdateInput',
    description: 'Input for updating a session filter',
  })

export type SessionFilterUpdate = z.infer<typeof sessionFilterUpdateSchema>

// Applied filter schema for frontend use
export const sessionAppliedFilterSchema = z.object({
  id: z.string(),
  type: z.string(),
  category: z.string(),
  name: z.string(),
  operator: z.string(),
  value: z.union([
    z.string(),
    z.array(z.string()),
    z.number(),
    z.boolean(),
    z.object({
      from: z.string().optional(),
      to: z.string().optional(),
      min: z.number().optional(),
      max: z.number().optional(),
    }),
  ]),
  logicalOperator: z.enum(['and', 'or']).default('and'),
  orderIndex: z.number().int().min(0).default(0),
  categoryLabel: z.string().optional(),
  filterLabel: z.string().optional(),
})

export type SessionAppliedFilter = z.infer<typeof sessionAppliedFilterSchema>

// Session query filter input for backend filtering
export const sessionQueryFiltersInputSchema = z
  .object({
    siteSessionTypeIds: z.array(z.string()).optional(),
    siteLocationIds: z.array(z.string()).optional(),
    siteLocationRoomIds: z.array(z.string()).optional(),
    staffUserIds: z.array(z.string()).optional(),
    categories: z.array(SessionCategoryEnum).optional(),
    clientIds: z.array(z.string()).optional(),
    excludeStartedSessions: z.boolean().optional(),
    recurringSessionsId: z.string().optional(),
  })
  .register(asObjectType, {
    name: 'SessionQueryFiltersInput',
    description: 'Filter input for session queries',
  })

export type SessionQueryFiltersInput = z.infer<typeof sessionQueryFiltersInputSchema>

// Filter list dialog schemas
export const selectedClientSchema = z.object({
  id: z.string(),
  name: z.string(),
})

export type SelectedClient = z.infer<typeof selectedClientSchema>

export const filterListFiltersSchema = z.object({
  siteLocationRoomIds: z.array(z.string()),
  categories: z.array(SessionCategoryEnum),
  siteSessionTypeIds: z.array(z.string()),
  staffUserIds: z.array(z.string()),
  clientIds: z.array(z.string()),
  selectedClients: z.array(selectedClientSchema).optional(),
})

export type FilterListFilters = z.infer<typeof filterListFiltersSchema>

export const filterListFormSchema = z.object({
  siteLocationRoomIds: z.array(z.string()).optional(),
  categories: z.array(SessionCategoryEnum).optional(),
  siteSessionTypeIds: z.array(z.string()).optional(),
  staffUserIds: z.array(z.string()).optional(),
  clientIds: z.array(z.string()).optional(),
})

export type FilterListFormData = z.infer<typeof filterListFormSchema>

export const MonthViewSessionSchema = z
  .object({
    sessionId: z.string(),
    name: z.string(),
    startTime: z.coerce.date(),
    color: z.string().nullable(),
    size: z.number().int(),
    bookedCount: z.number().int().nullable(),
    isBlockSlot: z.boolean(),
    instructor: z.array(CalendarSessionInstructorSchema).nullable(),
  })
  .register(asObjectType, {
    name: 'MonthViewSession',
    description: 'Minimal session data for month view display',
  })

export const MonthViewDaySchema = z
  .object({
    date: z.string(),
    sessions: z.array(MonthViewSessionSchema),
    totalCount: z.number().int(),
  })
  .register(asObjectType, {
    name: 'MonthViewDay',
    description: 'Day with sessions and total count for month view',
  })

export type MonthViewSession = z.infer<typeof MonthViewSessionSchema>
export type MonthViewDay = z.infer<typeof MonthViewDaySchema>

export const DaySessionsPageSchema = z
  .object({
    sessions: z.array(MonthViewSessionSchema),
    nextCursor: z.string().nullable(),
    hasNextPage: z.boolean(),
    totalCount: z.number().int(),
  })
  .register(asObjectType, {
    name: 'DaySessionsPage',
    description: 'Paginated sessions for a single day',
  })

export type DaySessionsPage = z.infer<typeof DaySessionsPageSchema>

export const WeekViewOverflowSessionSchema = z
  .object({
    sessionId: z.string(),
    name: z.string(),
    startTime: z.coerce.date(),
    endTime: z.coerce.date(),
    color: z.string().nullable(),
  })
  .register(asObjectType, {
    name: 'WeekViewOverflowSession',
    description: 'Minimal session data for week view overflow display',
  })

export const WeekViewDaySchema = z
  .object({
    date: z.string(),
    sessions: z.array(CalendarSessionSchema),
    overflowSessions: z.array(WeekViewOverflowSessionSchema),
    totalCount: z.number().int(),
  })
  .register(asObjectType, {
    name: 'WeekViewDay',
    description: 'Day with visible sessions (full) and overflow sessions (minimal) for week view',
  })

export type WeekViewOverflowSession = z.infer<typeof WeekViewOverflowSessionSchema>
export type WeekViewDay = z.infer<typeof WeekViewDaySchema>

export const DaySessionParamsSchema = z.object({
  siteId: z.string(),
  date: z.string(),
  timezone: z.string().optional(),
  status: z.string().optional(),
  cursorTime: z.string().optional(),
  cursorId: z.string().optional(),
  queryLimit: z.number().optional(),
  siteSessionTypeIds: z.array(z.string()).optional(),
  siteLocationIds: z.array(z.string()).optional(),
  siteLocationRoomIds: z.array(z.string()).optional(),
  categories: z.array(z.string()).optional(),
  staffUserIds: z.array(z.string()).optional(),
  clientIds: z.array(z.string()).optional(),
})

export type DaySessionParams = z.infer<typeof DaySessionParamsSchema>

export const CountRowSchema = z.object({
  totalCount: z.number(),
})

export type CountRow = z.infer<typeof CountRowSchema>

export const DaySessionRowSchema = z.object({
  sessionId: z.string(),
  name: z.string(),
  startTime: z.coerce.date(),
  color: z.string().nullable(),
  size: z.number().int(),
  bookedCount: z.number().int().nullable(),
  isBlockSlot: z.boolean(),
  instructor: z.array(CalendarSessionInstructorSchema).nullable(),
})

export type DaySessionRow = z.infer<typeof DaySessionRowSchema>

export const WeekViewBaseParamsSchema = z.object({
  siteId: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  timezone: z.string().optional(),
  status: z.string().optional(),
  siteSessionTypeIds: z.array(z.string()).optional(),
  siteLocationIds: z.array(z.string()).optional(),
  siteLocationRoomIds: z.array(z.string()).optional(),
  categories: z.array(z.string()).optional(),
  staffUserIds: z.array(z.string()).optional(),
  clientIds: z.array(z.string()).optional(),
})

export type WeekViewBaseParams = z.infer<typeof WeekViewBaseParamsSchema>

export const BaseSessionRowSchema = z.object({
  sessionId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  date: z.string(),
  startTime: z.coerce.date(),
  endTime: z.coerce.date(),
  siteLocationRoomId: z.string().nullable(),
  size: z.number(),
  waitlistEnabled: z.boolean(),
  status: z.string(),
  bookedCount: z.number().nullable(),
  waitlistCount: z.number(),
  isBlockSlot: z.boolean(),
  roomName: z.string().nullable(),
  sessionTypeColor: z.string().nullable(),
})

export type BaseSessionRow = z.infer<typeof BaseSessionRowSchema>

export const InstructorRowSchema = z.object({
  sessionId: z.string(),
  staffUserId: z.string(),
  name: z.string().nullable(),
})

export type InstructorRow = z.infer<typeof InstructorRowSchema>

export const BookingRowSchema = z.object({
  sessionId: z.string(),
  clientName: z.string().nullable(),
})

export type BookingRow = z.infer<typeof BookingRowSchema>

export const WaitlistCountRowSchema = z.object({
  sessionId: z.string(),
  waitlistCount: z.number(),
})

export type WaitlistCountRow = z.infer<typeof WaitlistCountRowSchema>

export const SessionQueryFiltersSchema = z.object({
  siteSessionTypeIds: z.array(z.string()).optional(),
  siteLocationIds: z.array(z.string()).optional(),
  siteLocationRoomIds: z.array(z.string()).optional(),
  staffUserIds: z.array(z.string()).optional(),
  clientIds: z.array(z.string()).optional(),
  categories: z.array(z.enum(['CLASS', 'PRIVATE', 'WORKSHOP'])).optional(),
})

export type SessionQueryFilters = z.infer<typeof SessionQueryFiltersSchema>

/**
 * Partial session data returned for check-in window queries
 * Contains only the fields needed for check-in processing (multi-session discovery)
 */
export type SessionForCheckIn = Pick<
  Session,
  'sessionId' | 'name' | 'startTime' | 'endTime' | 'siteSessionTypeId' | 'creditsRequired'
>
